import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { optimizeImage, imageUrls } from '../utils/imageOptimizer';
import SEO from '../components/SEO';

// Løsninger prioriteret (Neon sidst)
const solutions = [
  {
    id: 'facade',
    title: 'Facadeskilte',
    description: 'Synlig branding der skaber indtryk fra gaden. Vi designer og producerer facadeskiltning, der spiller sammen med både brand og arkitektur – fra elegante bogstaver til større installationer.',
    features: ['3D-bogstaver', 'Bakbelyste skilte', 'Logoer & symboler', 'Lysskilte'],
    image: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/mlqqmv3b_251206_NEONSIGN_CHP-160.jpg',
  },
  {
    id: 'led',
    title: 'LED-skilte',
    description: 'Energieffektiv skiltning med høj fleksibilitet. LED-teknologi giver mulighed for alt fra diskret belysning til dynamiske løsninger, der skaber opmærksomhed.',
    features: ['LED-bogstaver', 'Lysende rammer', 'Flexface skilte', 'Digital signage'],
    image: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/urnldo1l_251206_NEONSIGN_CHP-133.jpg',
  },
  {
    id: 'wayfinding',
    title: 'Wayfinding',
    description: 'Intuitive wayfinding-løsninger, der guider mennesker gennem komplekse miljøer. Vi skaber systemer, der fungerer i praksis – fra shoppingcentre til hospitaler og transport.',
    features: ['Skiltsystemer', 'Digitale info-standere', 'Retningsskilte', 'Oversigtskort'],
    image: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/08w4zlmg_251206_NEONSIGN_CHP-197.jpg',
  },
  {
    id: 'folie',
    title: 'Folie & Grafik',
    description: 'Kreativ transformation af overflader. Fra diskret afskærmning til visuelle identiteter integreret i rum og arkitektur – vi arbejder med løsninger til vinduer, vægge og interiør.',
    features: ['Vinduesfolie', 'Bilfoliering', 'Væggrafik', 'Gulvgrafik'],
    image: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/dfl0xumr_251206_NEONSIGN_CHP-238.jpg',
  },
  {
    id: 'system',
    title: 'Systemleverancer',
    description: 'Skalerbare løsninger til kæder og organisationer. Vi sikrer ensartet branding på tværs af lokationer med stærk projektledelse og effektiv udrulning.',
    features: ['Projektledelse', 'National udrulning', 'Lagerstyring', 'Service & vedligehold'],
    image: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/47tdp696_251206_NEONSIGN_CHP-68.jpg',
  },
  {
    id: 'neon',
    title: 'Neonskilte',
    description: 'Ægte håndlavet neon med tidløs æstetik. Produceret i Europa med fokus på kvalitet, holdbarhed og et udtryk, der ikke kan kopieres.',
    features: ['Ægte glasneon', 'Alle farver', 'Indendørs & udendørs', 'Restaurering'],
    image: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/728upq3i_251206_NEONSIGN_CHP-24.jpg',
  },
];

const processSteps = [
  {
    number: '01',
    title: 'Briefing & Rådgivning',
    description: 'Vi starter med at forstå projektets rammer, behov og mål. Med afsæt i jeres projekt rådgiver vi om den rigtige løsning – både funktionelt, visuelt og teknisk.',
  },
  {
    number: '02',
    title: 'Koncept & Design',
    description: 'Vi udvikler løsninger i tæt samarbejde med jer. Gennem visualiseringer og konkrete forslag sikrer vi, at designet kan realiseres – ikke kun tegnes.',
  },
  {
    number: '03',
    title: 'Produktion',
    description: 'Vi producerer med fokus på kvalitet, holdbarhed og præcision. Alle løsninger kvalitetssikres, så de lever op til krav, standarder og det aftalte.',
  },
  {
    number: '04',
    title: 'Installation & Service',
    description: 'Vi står for installationen og sikrer, at alt fungerer i praksis. Efterfølgende tilbyder vi service og vedligehold, så løsningen holder over tid.',
  },
];

const benefits = [
  'Én samlet leverandør til hele projektet',
  '+30 års erfaring med komplekse projekter',
  'Egen produktion i Danmark',
  'Fast kontaktperson gennem hele forløbet',
  'Dokumenteret leverancesikkerhed',
  'Service- og vedligeholdelsesaftaler',
];

export default function Solutions() {
  return (
    <main data-testid="solutions-page" className="pt-24">
      <SEO 
        title="Løsninger"
        pathname="/loesninger"
        description="Se vores komplette udvalg af skilteløsninger: Facadeskilte, LED-skilte, wayfinding, folie & grafik, systemleverancer og ægte neon. Professionel rådgivning og installation."
      />
      {/* Hero */}
      <section className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="max-w-3xl">
            <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6 animate-fade-in">
              Løsninger
            </p>
            <h1 
              data-testid="solutions-title"
              className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A] mb-6 animate-fade-in-up"
            >
              Skiltning og branding til projekter, hvor det skal fungere i praksis
            </h1>
            <p className="text-[#666666] text-lg leading-relaxed animate-fade-in-up stagger-2">
              Vi forener branding, skiltning og systematisk wayfinding i én samlet leverance – fra idé og design til produktion og montering. Vi er partneren, der sikrer, at løsningen bliver tænkt rigtigt, udført korrekt og leveret præcist som aftalt.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits bar */}
      <section className="bg-[#F8F8F8] py-12 border-y border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-[#1A1A1A] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-[#666666]">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution) => (
              <div
                key={solution.id}
                id={solution.id}
                data-testid={`solution-${solution.id}`}
                className="group"
              >
                <div className="aspect-[4/3] overflow-hidden mb-6 image-hover">
                  <img
                    src={imageUrls.medium(solution.image)}
                    alt={solution.title}
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-3">
                  {solution.title}
                </h3>
                <p className="text-[#666666] text-sm leading-relaxed">
                  {solution.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-[#F8F8F8] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
              Sådan arbejder vi
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-6">
              Fra idé til færdig installation
            </h2>
            <p className="text-[#666666] max-w-2xl mx-auto">
              Vi gør komplekse projekter enkle. Med en gennemtænkt proces og tæt dialog sikrer vi, at resultatet lever op til forventningerne – hver gang.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {processSteps.map((step) => (
              <div key={step.number}>
                <span className="font-heading text-5xl font-light text-[#E5E5E5]">
                  {step.number}
                </span>
                <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mt-4 mb-3">
                  {step.title}
                </h3>
                <p className="text-[#666666] text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kompleksitet Section */}
      <section className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
                Komplekse projekter
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-6">
                Vi varetager kompleksiteten, så du kan fokusere på kerneforretningen
              </h2>
              <p className="text-[#666666] leading-relaxed mb-6">
                Mange projekter kræver koordinering af flere løsninger: facadeskilte, indendørs wayfinding, digital signage og mere. Vi samler det hele under én leverance, med én kontaktperson og én faktura.
              </p>
              <p className="text-[#666666] leading-relaxed mb-8">
                Vores erfaring med større kæder, shoppingcentre og offentlige institutioner betyder, at vi forstår de krav der stilles til dokumentation, tidsplaner og kvalitetssikring.
              </p>
              <Link
                to="/cases"
                className="btn-outline inline-flex items-center gap-2"
              >
                Se eksempler på projekter
              </Link>
            </div>
            <div className="image-hover">
              <img
                src={imageUrls.large('https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/mlqqmv3b_251206_NEONSIGN_CHP-160.jpg')}
                alt="Komplekst projekt"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Haverkamp Partner Section */}
      <section className="bg-[#F8F8F8] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="image-hover aspect-[4/3] overflow-hidden">
                <img
                  src={imageUrls.large('https://customer-assets.emergentagent.com/job_82f36689-5905-4eb9-a410-52f80a0daaf2/artifacts/2nofv5q8_Z4HMqXZw.jpg')}
                  alt="Haverkamp folieløsninger"
                  className="w-full h-[108%] object-cover object-top"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
                Samarbejdspartner
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-6">
                Haverkamp – folieløsninger i verdensklasse
              </h2>
              <p className="text-[#666666] leading-relaxed mb-6">
                Som certificeret partner af Haverkamp tilbyder vi professionelle folieløsninger til vinduer og glas. Haverkamp er en førende tysk producent af højkvalitetsfolier til sikkerhed, solafskærmning, privatliv og dekoration.
              </p>
              <p className="text-[#666666] leading-relaxed mb-8">
                Vi anvender Haverkamps produkter i vores produktion, hvor det er relevant – og sikrer dermed en kvalitet og holdbarhed, der matcher de højeste standarder.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Sikkerhedsfilm og splintsikring',
                  'Solafskærmning og varmekontrol',
                  'Privatlivsfilm og dekoration',
                  'Switchable film (ClickFilm)',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#1A1A1A] rounded-full" />
                    <span className="text-[#666666] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="https://www.haverkamp.de"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1A1A1A] text-sm font-medium hover:underline inline-flex items-center gap-2"
              >
                Læs mere om Haverkamp
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1A1A1A] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-6">
            Har du et projekt, vi skal se på?
          </h2>
          <p className="text-white/60 mb-10 max-w-lg mx-auto">
            Lad os tage en uforpligtende snak om dine behov og muligheder. Vi rådgiver gerne – uanset projektets størrelse.
          </p>
          <Link
            to="/kontakt"
            data-testid="solutions-main-cta"
            className="btn-light inline-flex items-center gap-2"
          >
            Kontakt os
          </Link>
        </div>
      </section>
    </main>
  );
}
